import { format } from "date-fns";
import firebase from "firebase/app";

import { Button, Form, Header, Icon, Segment } from "semantic-ui-react";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PatternFormat } from "react-number-format";

import notify from "astrid-firebase/src/notifications";
import artifactProductionStatuses from "astrid-firestore/src/api/artifacts/constants/artifactProductionStatuses";
import deliverArtifacts from "astrid-firestore/src/api/artifacts/deliverArtifacts";
import getAcceptedArtifactFiles from "astrid-firestore/src/api/artifacts/getAcceptedArtifactFiles";
import distributeArticles from "astrid-firestore/src/api/distribution/distributeArticles";
import autoExport from "astrid-firestore/src/api/exports/autoExport";
import productionStatuses from "astrid-firestore/src/api/productions/constants/productionStatuses";
import updateProduction from "astrid-firestore/src/api/productions/updateProduction";
import createSubcontractorUploadedFilesTodoData from "astrid-firestore/src/api/todos/data/productions/createSubcontractorUploadedFilesTodoData";
import { setData } from "astrid-firestore/src/helpers";

import Flex from "../../../../components/Flex/Flex";
import DragAndDropContext from "../../../ui/components/DragAndDrop/DragAndDropContext";
import Draggable from "../../../ui/components/DragAndDrop/Draggable";
import Droppable from "../../../ui/components/DragAndDrop/Droppable";
import ErrorModal from "../../../ui/components/ErrorModal/ErrorModal";
import Label from "../../../ui/components/Label/Label";
import LoadingContext from "../../../ui/components/LoadingContext/LoadingContext";
import ModalPrompt from "../../../ui/components/ModalPrompt/ModalPrompt";
import useLoadingFunction from "../../../ui/hooks/useLoadingFunction";
import { getUrl } from "../../../ui/utils/url";

import ArticleSegment from "../../../articles/components/ArticleSegment/ArticleSegment";
import ArtifactFilesUploadButton from "../../../artifacts/components/ArtifactFilesUpload/ArtifactFilesUploadButton";
import ArtifactFilesUploadForm from "../../../artifacts/components/ArtifactFilesUpload/ArtifactFilesUploadForm";
import ArtifactSegment from "../../../artifacts/components/ArtifactSegment/ArtifactSegment";
import getArtifactProductionStatus from "../../../artifacts/utils/getArtifactProductionStatus";
import { useProfile } from "../../../authentication/state/profile";
import useHasRight from "../../../authorization/hooks/useHasRight";

import ProductionArticlesFinalizedModal from "./components/ProductionArticlesFinalizedModal";
import useProductionArtifactDropdownMenuOptions from "./hooks/useProductionArtifactDropdownMenuOptions";
import useProductionArtifactManager from "./hooks/useProductionArtifactManager";

export default function ProductionArtifactsView({
	producer,
	publisher,
	production,
	articles,
	artifacts,
	productionTodos,
}) {
	const { t } = useTranslation();
	const user = useProfile();
	const hasRight = useHasRight();

	const canDeliver =
		production.status === productionStatuses.DONE ? hasRight("productions.admin") : hasRight("productions.deliver");

	const unresolvedTodos = productionTodos?.filter((todo) => !todo.resolved) || [];

	const [files, setFiles] = useState(null);
	const [draggableId, setDraggableId] = useState(null);
	const [showFinalizedModal, setShowFinalizedModal] = useState(false);
	const [openDurationModal, setOpenDurationModal] = useState(false);
	const [deliveryDuration, setDeliveryDuration] = useState();
	const [deliveringArticleId, setDeliveringArticleId] = useState();

	const { isDirty, hasArtifacts, moveArtifact, articleSegments, unassignedArtifacts, totalDuration, loading, error } =
		useProductionArtifactManager({
			articles,
			artifacts,
			production,
		});

	const getArtifactDropdownMenuOptions = useProductionArtifactDropdownMenuOptions({
		canDeliver,
		articles,
		production,
		moveArtifact,
	});

	const onDragStart = ({ draggableId }) => {
		setDraggableId(draggableId);
	};

	const onDragEnd = ({ draggableId, destination }) => {
		setDraggableId(null);

		moveArtifact(draggableId, destination.droppableId, destination.index);
	};

	const [onClickDeliver, deliverLoading, deliverError] = useLoadingFunction(
		async (article) => {
			setDeliveringArticleId(article?.id);

			const { finalized, readyArticles, exportableArticlesWithArtifact, deliveryDuration } =
				await deliverArtifacts(firebase, {
					publisher,
					productionId: production.id,
					user,
					article,
				});

			if (readyArticles?.length > 0) {
				await distributeArticles(firebase, { articles: readyArticles });
			}

			if (exportableArticlesWithArtifact?.length > 0) {
				await autoExport(firebase, { publisher, articlesWithArtifact: exportableArticlesWithArtifact });
			}

			return { finalized, deliveryDuration };
		},
		{
			onSuccess({ finalized, deliveryDuration }) {
				if (finalized) {
					setShowFinalizedModal(deliveryDuration);
				}
			},
		},
	);

	const onArtifactFilesUploadSuccess = async ({ files }) => {
		if (hasRight("subcontractor")) {
			const lastNotified = production.subcontractorUploadNotification?.toDate();
			const anHourAgo = new Date(Date.now() - 1000 * 60 * 60);

			if (production.manager?.length && (!lastNotified || lastNotified < anHourAgo)) {
				notify(production.manager, (user) => {
					const lang = user.languageCode || "en";

					return {
						subject: t("notifyUploadedFilesSubject", "Subcontractor uploaded new files", { lng: lang }),
						message: t("notifyUploadedFilesMessage", {
							lng: lang,
							title: production.title,
							url: getUrl(`production/${production.id}/artifacts`),
						}),
					};
				});

				await production.ref.update({
					subcontractorUploadNotification: firebase.firestore.FieldValue.serverTimestamp(),
				});
			}

			const todo = createSubcontractorUploadedFilesTodoData(firebase, {
				production,
				files: {
					parking: {
						external: false,
						length: files.length,
					},
				},
			});

			await setData(todo.ref, todo);
		}
	};

	const updateDuration = () => {
		if (deliveryDuration) {
			return updateProduction(firebase, {
				ref: production.ref,
				deliveryDuration,
			});
		}
	};

	return (
		<LoadingContext loading={loading} error={error}>
			{canDeliver && hasArtifacts && (
				<Button basic circular size="small" onClick={() => setOpenDurationModal(true)}>
					{t("deliveryDuration", "Delivery duration")}: {production.deliveryDuration || totalDuration}
					<Icon name="pencil" fitted style={{ paddingLeft: 7, fontSize: "0.8rem" }} />
				</Button>
			)}
			<DragAndDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
				{articleSegments.map(({ article, artifact, acceptedArtifactIds }) => {
					const hasDeliveryDate = production.articles?.[article.id]?.deliveryDate?.toDate();

					return (
						<ArticleSegment
							key={article.id}
							article={article}
							extra={
								<Flex gap={10}>
									{hasDeliveryDate && (
										<div style={{ opacity: 0.4, fontSize: "0.9em" }}>{`${t(
											"deliveryDate",
											"Delivery date",
										)}: ${format(
											production.articles?.[article.id]?.deliveryDate?.toDate(),
											"yyyy-MM-dd",
										)}`}</div>
									)}

									{artifact?.article ? (
										<Label basic size="tiny">
											{t("delivered", "Delivered")}
										</Label>
									) : (
										canDeliver && (
											<Button
												size="mini"
												color="green"
												disabled={!artifact || deliverLoading || unresolvedTodos.length > 0}
												loading={deliverLoading && deliveringArticleId === article.id}
												onClick={() => onClickDeliver(article)}
											>
												{t("deliver", "Deliver")}
											</Button>
										)
									)}
								</Flex>
							}
						>
							{hasArtifacts && (
								<Droppable
									id={article.id}
									text={t("filesToDeliver", "Files to deliver")}
									disabled={!canDeliver || !acceptedArtifactIds.includes(draggableId) || !!artifact}
								>
									{artifact && (
										<Draggable
											id={artifact.id}
											key={artifact.key}
											index={0}
											disabled={
												getArtifactProductionStatus({ artifact }) ===
												artifactProductionStatuses.DELIVERED
											}
										>
											<ArtifactSegment
												article={article}
												artifact={artifact}
												dropdownMenuOptions={getArtifactDropdownMenuOptions({
													artifact,
													article,
												})}
												selected
											/>
										</Draggable>
									)}
								</Droppable>
							)}
						</ArticleSegment>
					);
				})}

				<Segment style={{ background: "transparent" }}>
					<Header as="h5" style={{ margin: 0 }}>
						{t("parking", "Parking")}
					</Header>

					<Droppable
						id="unassigned"
						text={t("dropFilesHere", "Drop files here")}
						accept={getAcceptedArtifactFiles()}
					>
						{unassignedArtifacts.map((artifact, index) => (
							<Draggable key={artifact.id} id={artifact.id} index={index}>
								<ArtifactSegment
									artifact={artifact}
									dropdownMenuOptions={getArtifactDropdownMenuOptions({ artifact })}
								/>
							</Draggable>
						))}
					</Droppable>
				</Segment>
			</DragAndDropContext>
			<Flex gap={10} justifyContent="flex-start">
				<ArtifactFilesUploadButton
					producer={producer}
					production={production}
					onSuccess={onArtifactFilesUploadSuccess}
				/>

				{canDeliver && (
					<Button
						positive
						onClick={() => onClickDeliver()}
						loading={deliverLoading && !deliveringArticleId}
						disabled={!isDirty || deliverLoading || unresolvedTodos.length > 0}
						content={t("deliverFiles", "Deliver files")}
					/>
				)}

				{unresolvedTodos.length > 0 && (
					<Label basic>
						{t("todosResolvedBeforeDelivery", "{{num}} todos needs to be resolved before delivery", {
							num: unresolvedTodos.length,
						})}
					</Label>
				)}
			</Flex>
			{showFinalizedModal && (
				<ProductionArticlesFinalizedModal
					showFinalizedModal={showFinalizedModal}
					setShowFinalizedModal={setShowFinalizedModal}
				/>
			)}
			{deliverError && <ErrorModal error={deliverError} />}
			{files && (
				<ArtifactFilesUploadForm
					producer={producer}
					production={production}
					defaultName={production?.title}
					defaultFiles={files}
					onClose={() => setFiles(null)}
					onSuccess={onArtifactFilesUploadSuccess}
				/>
			)}
			{openDurationModal && (
				<ModalPrompt
					size="mini"
					onSave={() => updateDuration()}
					onClose={() => setOpenDurationModal(false)}
					disabled={!deliveryDuration}
				>
					<Form>
						<Form.Input label={t("deliveryDuration", "Delivery duration")}>
							<PatternFormat
								value={production.deliveryDuration || totalDuration}
								onValueChange={({ formattedValue }) => {
									return setDeliveryDuration(formattedValue);
								}}
								format={"##:##:##"}
								allowEmptyFormatting
								mask="_"
							/>
						</Form.Input>
					</Form>
				</ModalPrompt>
			)}
		</LoadingContext>
	);
}
